"use client";
import { Send } from "lucide-react";

import { memo, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// Firebase imports
import { ConversationService } from "@/firebase/conversation";
import { onSnapshot, Timestamp, Unsubscribe } from "firebase/firestore";
import { useTranslations } from "next-intl";
import ChatDivider from "@/src/app/all-units/[unit]/chat-with-owner/components/ChatDivider";
import ChatBubble from "@/src/app/all-units/[unit]/chat-with-owner/components/ChatBubble";
import GoToLastMessage from "@/src/app/all-units/[unit]/chat-with-owner/components/BackToTop";
import { Input } from "../ui/input";

interface MessageType {
  body: string;
  id: string;
  senderId: string;
  senderName: string;
  sentAt: Timestamp;
}

interface MessageStateType {
  msg: string;
  msgs: MessageType[];
}

interface DialogType {
  createdAt: Timestamp;
  id: string;
  lastMessage: string;
  lastMessageSenderId: string;
  lastMessageTime: Timestamp;
  ownerFullName: string;
  ownerId: string;
  participants: string[];
  propertyId: string;
  propertyImage: string;
  propertyTitle: string;
  userFullName: string;
  userId: string;
}

const ChatWithGuest = ({ selectedDialog }: { selectedDialog: DialogType }) => {
  const [messageState, setMessageState] = useState<MessageStateType>({
    msg: "",
    msgs: [],
  });
  const msgsContainerRef = useRef<HTMLDivElement>(null);
  let unsubscribeMessages: Unsubscribe | null = null;
  const t = useTranslations();

  // Get messages for the selected conversation
  useEffect(() => {
    if (selectedDialog?.id) {
      if (!selectedDialog?.id) {
        console.error("No conversation ID provided");
        return;
      }
      const messagesQuery = ConversationService.getMessagesQuery(
        selectedDialog.id
      );
      unsubscribeMessages = onSnapshot(messagesQuery, (querySnapshot) => {
        const msgs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MessageType[];
        setMessageState((prev) => ({ ...prev, msgs }));
      });
    }
    return () => {
      unsubscribeMessages?.();
      setMessageState((prev) => ({ ...prev, msgs: [] }));
    };
  }, [selectedDialog?.id]);

  // Handle sending a new message
  const handleMessage = async (e: any) => {
    e.preventDefault();
    if (!navigator.onLine) {
      toast.error(t("errorCheckingConnection"));
      return;
    }
    if (!messageState.msg || !selectedDialog?.id) {
      return;
    }

    try {
      setMessageState((prev) => ({
        ...prev,
        msg: "",
      }));
      // Send message using Firebase
      await ConversationService.sendMessage(selectedDialog.id, {
        senderId: selectedDialog.userId,
        senderName: selectedDialog.userFullName,
        body: messageState.msg,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(t("errorSendingMessage"));
    }
  };

  useEffect(() => {
    msgsContainerRef?.current?.scrollTo({
      top: msgsContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messageState.msgs]);

  return (
    <div className="flex h-full flex-col items-center w-full mb-4 relative overflow-hidden">
      {/* <FloatDateWidget {...{ currentDate, showWidget }} /> */}
      <div
        ref={msgsContainerRef}
        className="w-full h-full flex flex-col overflow-x-hidden overflow-y-auto px-4 py-2"
      >
        {messageState.msgs
          .sort(
            (a, b) =>
              a?.sentAt.toDate().getTime() - b?.sentAt.toDate().getTime()
          )
          .map((msg, i) => (
            <div key={msg?.id}>
              <ChatDivider
                currentMsgId={msg.sentAt.toDate().getTime()}
                prevMsgId={messageState.msgs[i - 1]?.sentAt.toDate().getTime()}
              />
              <ChatBubble
                id={msg?.sentAt.toDate().getTime()}
                msg={msg.body || ""}
                senderId={msg.senderId}
                userId={selectedDialog?.userId}
              />
            </div>
          ))}
        <GoToLastMessage {...{ msgsContainerRef }} />
      </div>

      <form
        className="flex w-full bg-white border group rounded-xl items-center mt-4 gap-2 px-4"
        onSubmit={handleMessage}
      >
        <Input
          type="text"
          value={messageState.msg}
          className="flex-1 rounded-none !ring-offset-0 !border-none h-10 !p-0 !outline-none !ring-0 "
          onChange={(e) =>
            setMessageState((prev) => ({
              ...prev,
              msg: e.target.value,
            }))
          }
        />
        <button>
          <Send />
        </button>
      </form>
    </div>
  );
};

export default memo(ChatWithGuest);
